import { createRouter, createWebHistory, type RouteRecordName } from 'vue-router'

import { AllowedPages, InternalPages } from '@/enums/allowedPages'
import { isAuthenticated } from '@/libs/auth'
import { navItems } from '@/navigation'
import { useAppStore } from '@/stores/app'

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior() {
        return {
            top: 1,
            behavior: 'smooth'
        }
    },
    routes: [
        {
            path: '/',
            name: 'home',
            component: () => import('@/views/Distributor.vue'),
            meta: {
                requiresAuth: true,
                page: InternalPages.Home
            }
        },
        {
            path: '/profil',
            name: 'profil',
            component: () => import('@/views/Profil.vue'),
            meta: {
                requiresAuth: true,
                page: InternalPages.Profil
            }
        },
        {
            path: '/spremeni-geslo',
            name: 'change-password',
            component: () => import('@/views/account/ChangePassword.vue'),
            meta: {
                requiresAuth: true,
                page: InternalPages.Profil
            }
        },
        {
            path: '/kartica-obracunov-s-placili',
            name: 'card-traffic',
            component: () => import('@/views/CardTraffic.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.CardTraffic
            }
        },
        {
            path: '/odprte-postavke',
            name: 'open-items',
            component: () => import('@/views/OpenItems.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.OpenItems
            }
        },
        {
            path: '/odcitki',
            name: 'readings',
            component: () => import('@/views/Readings.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.Readings
            }
        },
        {
            path: '/rezervni-sklad',
            name: 'reserve-fund',
            component: () => import('@/views/funds/ReserveFund.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.ReserveFund
            }
        },
        {
            path: '/prostovoljni-sklad',
            name: 'voluntary-fund',
            component: () => import('@/views/funds/VoluntaryFund.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.VoluntaryFund
            }
        },
        {
            path: '/dokumenti-upravnika',
            name: 'manager-documents',
            component: () => import('@/views/documents/ManagerDocuments.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.ManagerDocuments
            }
        },
        {
            path: '/dokumenti-upravnika/:id_vlozisca',
            name: 'manager-documents-vlozisce',
            component: () => import('@/views/documents/Depositories.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.ManagerDocuments
            }
        },
        {
            path: '/dokumenti-dobaviteljev',
            name: 'supplier-documents',
            component: () => import('@/views/documents/SupplierDocuments.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.SupplierDocuments
            }
        },
        {
            path: '/obvestila',
            name: 'notifications',
            component: () => import('@/views/Notifications.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.Notifications
            }
        },
        {
            path: '/vase-enote',
            name: 'your-units',
            component: () => import('@/views/YourUnits.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.YourUnits
            }
        },
        {
            path: '/oglasna-deska',
            name: 'bulletin-board',
            component: () => import('@/views/Bulletins.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.BulletinBoard
            }
        },
        {
            path: '/pogosta-vprasanja',
            name: 'faq',
            component: () => import('@/views/Faqs.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.Faq
            }
        },
        {
            path: '/izvajalci',
            name: 'contractors',
            component: () => import('@/views/Contractors.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.Contractors
            }
        },
        {
            path: '/specifikacija-stroskov',
            name: 'cost-specification',
            component: () => import('@/views/CostSpecification.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.CostSpecification
            }
        },
        {
            path: '/dolzniki-rezervnega-sklada',
            name: 'reserve-fund-debtors',
            component: () => import('@/views/funds/ReserveFundDebtors.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.ReservedFundDebtors
            }
        },
        {
            path: '/promet-knjizb-rezervnega-sklada',
            name: 'turnover-reserve-fund-books',
            component: () => import('@/views/funds/TurnoverReserveFundBooks.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.ReservedFundPostings
            }
        },
        {
            path: '/odprte-postavke-najemnikov',
            name: 'tenants-open-items',
            component: () => import('@/views/Tenants.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.Tenants
            }
        },
        {
            path: '/redni-obracun',
            name: 'regular-billing',
            component: () => import('@/views/RegularBilling.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.RegularBilling
            }
        },
        {
            path: '/obracun-rezervni-sklad',
            name: 'reserve-fund-billing',
            component: () => import('@/views/ReserveFundBilling.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.ReserveFundBilling
            }
        },
        {
            path: '/prijavljena-stevila-oseb-in-psov',
            name: 'registered-number-of-people-and-dogs',
            component: () => import('@/views/RegisteredNumberOfPeopleAndDogs.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.RegisteredNumberOfPeopleAndDogs
            }
        },
        {
            path: '/zavarovalne-pogodbe',
            name: 'insurance-contracts',
            component: () => import('@/views/InsuranceContracts.vue'),
            meta: {
                requiresAuth: true,
                page: AllowedPages.InsuranceContracts
            }
        },
        {
            path: '/obrazci',
            name: 'forms',
            component: () => import('@/views/Forms.vue'),
            meta: {
                requiresAuth: true,
                page: InternalPages.Forms
            }
        },
        {
            path: '/prijava',
            name: 'login',
            component: () => import('@/views/account/Login.vue'),
            meta: {
                layout: 'Blank',
                requiresAuth: false,
                page: InternalPages.Login
            }
        },
        {
            path: '/registracija',
            name: 'register',
            component: () => import('@/views/account/Register.vue'),
            meta: {
                layout: 'Blank',
                requiresAuth: false,
                page: InternalPages.Register
            }
        },
        {
            path: '/pozabljeno-geslo',
            name: 'forgotten-password',
            component: () => import('@/views/account/ForgottenPassword.vue'),
            meta: {
                layout: 'Blank',
                requiresAuth: false,
                page: InternalPages.ForgottenPassword
            }
        },
        {
            path: '/pozabljeno-geslo/:token',
            name: 'reset-password',
            component: () => import('@/views/account/ResetPassword.vue'),
            meta: {
                layout: 'Blank',
                requiresAuth: false,
                page: InternalPages.ResetPassword
            }
        },
        {
            path: '/dodaj-upravnika',
            name: 'add-manager',
            component: () => import('@/views/account/AddManager.vue'),
            meta: {
                requiresAuth: true,
                page: InternalPages.AddManager
            }
        },
        {
            path: '/povezi-upravnika',
            name: 'connect-manager',
            component: () => import('@/views/account/ConnectManager.vue'),
            meta: {
                layout: 'Blank',
                requiresAuth: false,
                page: InternalPages.Login
            }
        },
        {
            path: '/login-as-user/:token',
            name: 'login-as-user',
            component: () => import('@/views/account/LoginAsUser.vue'),
            meta: {
                layout: 'Blank',
                requiresAuth: false,
                page: InternalPages.LoginAsUser
            }
        },
        {
            path: '/ni-najdeno',
            name: 'not-found',
            component: () => import('@/views/NotFound.vue'),
            meta: {
                layout: 'Blank',
                requiresAuth: false
            }
        },
        {
            path: '/:pathMatch(.*)*',
            redirect: '/ni-najdeno'
        }
    ]
})
export const checkRoute = (isLoggedIn: boolean): RouteRecordName | null | undefined => {
    const allowedPagesStr: string | null = localStorage.getItem('allowedPages') || '[]'
    const allowedPages: number[] = JSON.parse(allowedPagesStr)
    let routeName: RouteRecordName | null | undefined = isLoggedIn ? 'not-found' : 'login'
    for (const navItem of navItems.value) {
        if (navItem.page && allowedPages.includes(navItem.page)) {
            routeName = navItem.to
            break
        }
    }
    return routeName
}
router.beforeEach((to) => {
    const loggedIn: boolean = isAuthenticated()
    const appStore = useAppStore()
    const allowedPagesStr: string | null = localStorage.getItem('allowedPages') || '[]'
    const allowedPages: number[] = JSON.parse(allowedPagesStr)

    if (loggedIn && to.name === 'login') {
        return { name: 'home' }
    }

    if (to.name === 'tenant-open-items' && !useAppStore().ifUserHasTenants()) {
        return { name: 'not-found' }
    }

    if (to.meta.page && !allowedPages.includes(to.meta.page) && to.name !== 'not-found') {
        return { name: String(checkRoute(loggedIn)) }
    }

    if (to.meta.requiresAuth && !loggedIn) {
        appStore.setLogout()
        return { name: 'login' }
    }
})

export default router

